<template>
  <div class="wgt-article-waterfall" :style="setstyle">
    <ArticleWaterfall :key="`normal-waterfall-${normalListSignature}`" :list="normalDisplayList" />
  </div>
</template>

<script>
import { getArticleManagementList } from '@/api/article'
import config from './config'
import ArticleWaterfall from './article-waterfall.vue'

const DEFAULT_PAGE_SIZE = 200

function parseMaybeJSON(val) {
  if (!val) return null
  if (typeof val === 'string') {
    try {
      return JSON.parse(val)
    } catch (e) {
      return null
    }
  }
  return val
}

function extractListPayload(response) {
  const payload = (response && response.data && response.data.data) || (response && response.data) || {}
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload.list)) return payload.list
  if (Array.isArray(payload.rows)) return payload.rows
  return []
}

function normalizeCategoryId(data) {
  if (data == null || data === '') return ''
  if (Array.isArray(data)) {
    const first = data[0] || {}
    return first.category_id || first.id || ''
  }
  if (typeof data === 'object') {
    return data.category_id || data.id || ''
  }
  return data
}

function normalizeEmbeddedArticleList(val) {
  if (!val || typeof val !== 'object') return []
  const raw = Array.isArray(val.articles) ? val.articles : Array.isArray(val.list) ? val.list : []
  if (!raw.length) return []
  return raw.map((item) => ({
    id: item.id || item.article_id,
    title: item.title || item.article_title || '',
    image_url: item.image_url || item.url || item.cover_img || '',
    article_type: item.article_type || '',
    content: item.content,
    materials: item.materials
  }))
}

export default {
  name: 'ArticleBlock',
  wgtName: '文章',
  wgtDesc: '',
  wgtIcon: 'wgt-article',
  wgtType: 1,
  config,
  components: {
    ArticleWaterfall
  },
  props: {
    value: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      articleList: [],
      lastNormalFetchCategoryId: ''
    }
  },
  computed: {
    setstyle() {
      const v = this.value || {}
      const p = v.padded || {}
      return {
        padding:
          (p.paddedt || 0) +
          'px ' +
          (p.paddedr || 0) +
          'px ' +
          (p.paddedb || 0) +
          'px ' +
          (p.paddedl || 0) +
          'px',
        backgroundColor: v.background && v.background.color ? v.background.color : 'transparent',
        backgroundImage: v.background && v.background.imageUrl ? `url(${v.background.imageUrl})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }
    },
    embeddedArticleList() {
      const data = this.value && this.value.data
      return this.filterDisplayList(normalizeEmbeddedArticleList(data))
    },
    normalDisplayList() {
      const fetchedList = this.filterDisplayList(this.articleList)
      if (fetchedList.length) return fetchedList
      return this.embeddedArticleList
    },
    normalListSignature() {
      return this.normalDisplayList.map((item, index) => `${index}-${item.id || ''}-${item.title || ''}`).join('|')
    }
  },
  watch: {
    value: {
      async handler() {
        await this.syncArticleDataFromValue()
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    async syncArticleDataFromValue(force = false) {
      const categoryId = this.getNormalModeCategoryId()
      if (!categoryId) {
        this.articleList = []
        this.lastNormalFetchCategoryId = ''
        return
      }
      if (!force && categoryId === this.lastNormalFetchCategoryId && this.articleList.length) return
      await this.fetchArticlesByCategory(categoryId, true)
      this.lastNormalFetchCategoryId = categoryId
    },
    getNormalModeCategoryId() {
      return normalizeCategoryId(this.value && this.value.data)
    },
    filterDisplayList(list) {
      if (!Array.isArray(list) || !list.length) return []
      return list.filter((x) => {
        if (!x) return false
        const hasImg = !!x.image_url
        const hasTitle = !!(x.title && String(x.title).trim())
        return hasImg || hasTitle
      })
    },
    async requestArticlesByCategory(categoryId) {
      if (!categoryId) return []
      const response = await getArticleManagementList({
        category_id: categoryId,
        page: 1,
        pageSize: DEFAULT_PAGE_SIZE
      })
      const list = extractListPayload(response)
      return list.map((item) => {
        const articleId = item.article_id || item.id
        return {
          id: articleId,
          title: item.title || item.article_title || '',
          image_url: this.resolveArticleCover(item),
          article_type: item.article_type || '',
          content: item.content,
          materials: item.materials
        }
      })
    },
    async fetchArticlesByCategory(categoryId, force = false) {
      if (!categoryId) {
        this.articleList = []
        return
      }
      if (!force && categoryId === this.lastNormalFetchCategoryId && this.articleList.length) return
      try {
        const mapped = await this.requestArticlesByCategory(categoryId)
        this.articleList = mapped
      } catch (e) {
        this.articleList = []
      }
    },
    resolveArticleCover(article) {
      if (!article || typeof article !== 'object') return ''
      if (article.image_url) return article.image_url
      if (article.cover_img) return article.cover_img
      if (article.cover) return article.cover
      if (article.url) return article.url
      const content = parseMaybeJSON(article.content) || parseMaybeJSON(article.materials)
      if (!Array.isArray(content) || !content.length) return ''
      const first = content[0] || {}
      return first.material_url || first.url || first.cover_img || ''
    }
  }
}
</script>

<style lang="scss" scoped>
.wgt-article-waterfall {
  box-sizing: border-box;
  width: 100%;
  min-height: 120px;
}
</style>
